import React, { createContext, useContext, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { services } from "@/mock/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteContext = createContext(null);
export const useQuote = () => useContext(QuoteContext);

export function QuoteProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });

  const openQuote = () => setOpen(true);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/quotes`, form);
      toast.success("Thank you! Our team will reach out within one business day.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <QuoteContext.Provider value={{ openQuote }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-navy2 border-white/10 text-white sm:max-w-[480px]">
          <DialogHeader>
            <div className="flex items-center gap-2 text-brand-lime mb-1">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-wider">Request a Quote</span>
            </div>
            <DialogTitle className="font-display text-2xl">Let's secure your business</DialogTitle>
            <DialogDescription className="text-slate-400">
              Tell us what you need and our Halifax team will prepare a tailored proposal.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="q-name">Full name *</Label>
                <Input id="q-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10" placeholder="Jane Doe" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-email">Work email *</Label>
                <Input id="q-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-white/5 border-white/10" placeholder="jane@company.ca" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-company">Company</Label>
              <Input id="q-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="bg-white/5 border-white/10" placeholder="Company Inc." />
            </div>
            <div className="space-y-1.5">
              <Label>Service of interest</Label>
              <Select value={form.service} onValueChange={(v) => set("service", v)}>
                <SelectTrigger className="bg-white/5 border-white/10"><SelectValue placeholder="Select a service" /></SelectTrigger>
                <SelectContent className="bg-brand-navy2 border-white/10 text-white">
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-msg">How can we help?</Label>
              <Textarea id="q-msg" value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-white/5 border-white/10 min-h-[90px]" placeholder="Briefly describe your requirements..." />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold rounded-full disabled:opacity-70">
              {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>) : "Submit request"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </QuoteContext.Provider>
  );
}
